import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, CircularProgress, Alert, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '../../components/Layout/MainLayout';
import axios from 'axios';
import { CompanyProfileDisplay } from '../../components/CompanyProfile';
import {
  CompanyBasicInfo,
  CompanyProfile as CompanyProfileType,
} from '../../types/company-profile';

interface CompanyProfileResponseData {
  company: CompanyBasicInfo;
  profile: CompanyProfileType;
}

type BackOrigin = 'companies' | 'timeline' | 'messages' | 'admin-companies';

interface LocationState {
  from?: BackOrigin;
}

const BACK_TARGETS: Record<BackOrigin, { path: string; label: string }> = {
  companies: { path: '/companies', label: '企業一覧に戻る' },
  timeline:  { path: '/timeline',  label: 'タイムラインに戻る' },
  messages:  { path: '/messages',  label: 'メッセージ一覧に戻る' },
  'admin-companies': { path: '/admin/companies-preview', label: '企業一覧に戻る' },
};

const CompanyProfileViewPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromOrigin: BackOrigin = useMemo(() => {
    const state = location.state as LocationState | null;
    const from = state?.from;
    return from && from in BACK_TARGETS ? from : 'timeline';
  }, [location.state]);
  const backTarget = BACK_TARGETS[fromOrigin];
  const [profile, setProfile] = useState<CompanyProfileResponseData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!companyId) {
        setError('企業IDが指定されていません');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/companies/${companyId}/profile`);
        if (response.data.success) {
          setProfile(response.data.data);
          setIsPrivate(false);
        } else if (response.data.info?.code === 'PROFILE_PRIVATE') {
          // 非公開状態は正常な状態として扱う
          setIsPrivate(true);
          setError(response.data.info.message);
        } else {
          setError('企業プロフィールの取得に失敗しました');
        }
      } catch (err: any) {
        console.error('企業プロフィール取得エラー:', err);
        const serverMessage =
          err.response?.data?.error?.message || err.message || null;
        if (err.response?.status === 404) {
          setError(serverMessage || '企業プロフィールが見つかりません');
        } else if (err.response?.status === 403) {
          setIsPrivate(true);
          setError(serverMessage || 'この企業プロフィールは現在非公開です。');
        } else {
          setError(serverMessage || '企業プロフィールの取得に失敗しました');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [companyId]);

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity={isPrivate ? 'info' : 'error'} sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backTarget.path)}
            variant="outlined"
          >
            {backTarget.label}
          </Button>
        </Container>
      </MainLayout>
    );
  }

  if (!profile) {
    return (
      <MainLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="warning">企業プロフィールが見つかりません</Alert>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* 戻るボタン */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backTarget.path)}
            variant="outlined"
            size="small"
          >
            {backTarget.label}
          </Button>
        </Box>
        <CompanyProfileDisplay
          company={profile.company}
          profile={profile.profile as any}
          variant="public"
        />
      </Container>
    </MainLayout>
  );
};

export default CompanyProfileViewPage;
